import React, { Component } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';

import { emailIntegrationCsv } from 'utils/apis/integration';
import { EMAIL_REGEX } from 'utils/constants';

const normalizeEmail = value => (value || '').trim().toLowerCase();

class IntegrationCsvModal extends Component {
  static propTypes = {
    closeModal: PropTypes.func.isRequired,
    visible: PropTypes.bool.isRequired,
    integrationType: PropTypes.oneOf(['listing', 'hotel']).isRequired,
    defaultEmail: PropTypes.string,
    filters: PropTypes.shape({
      unitSearch: PropTypes.string,
      propertySearch: PropTypes.string,
      sortBy: PropTypes.string,
      sortOrder: PropTypes.string
    })
  };

  static defaultProps = {
    closeModal: () => {},
    visible: false,
    defaultEmail: '',
    filters: {}
  };

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false
    };
  }

  componentDidUpdate(prevProps) {
    const { visible, defaultEmail, form } = this.props;

    if (visible && !prevProps.visible && defaultEmail) {
      form.setFieldsValue({ email: defaultEmail });
    }
  }

  getTypeLabel = () => {
    const { integrationType } = this.props;
    return integrationType === 'listing'
      ? intl.get('hostConnect.integration.integrationType.Listing Integration').d('Listing Integration')
      : intl.get('hostConnect.integration.integrationType.Hotel Integration').d('Hotel Integration');
  };

  getActiveFilterSummary = () => {
    const { filters = {}, integrationType } = this.props;
    const parts = [];

    if (integrationType === 'listing' && filters.unitSearch) {
      parts.push(`unit: "${filters.unitSearch}"`);
    }
    if (filters.propertySearch) {
      parts.push(`property: "${filters.propertySearch}"`);
    }

    return parts;
  };

  handleSubmit = () => {
    const { form, closeModal, integrationType, filters = {} } = this.props;

    form.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      this.setState({ isLoading: true });

      try {
        const email = normalizeEmail(values.email);
        await emailIntegrationCsv({
          email,
          type: integrationType,
          unitSearch: filters.unitSearch || undefined,
          propertySearch: filters.propertySearch || undefined,
          sortBy: filters.sortBy || undefined,
          sortOrder: filters.sortOrder || undefined
        });

        notification.success({
          message: intl.get('hostConnect.integration.csvModal.successTitle').d('CSV export started'),
          description: intl
            .get('hostConnect.integration.csvModal.successDesc')
            .d('Your integration CSV is being generated and will be emailed to you shortly.')
        });

        closeModal();
      } catch (ex) {
        console.log(ex, 'Error occurred when requesting integration CSV by email.');
        notification.error({
          message: intl.get('hostConnect.integration.csvModal.errorTitle').d('CSV export failed'),
          description:
            ex.message || intl.get('hostConnect.integration.csvModal.errorDesc').d('Unable to start the CSV export. Please try again later.')
        });
      } finally {
        this.setState({ isLoading: false });
      }
    });
  };

  render() {
    const { visible, closeModal, form } = this.props;
    const { isLoading } = this.state;
    const { getFieldDecorator } = form;
    const typeLabel = this.getTypeLabel();
    const activeFilters = this.getActiveFilterSummary();

    return (
      <Modal
        title={intl.get('hostConnect.integration.csvModal.title').d('Export Integration CSV')}
        visible={visible}
        onCancel={closeModal}
        onOk={this.handleSubmit}
        confirmLoading={isLoading}
        okText={intl.get('hostConnect.integration.csvModal.send').d('Send to Email')}
        cancelText={intl.get('hostConnect.integration.csvModal.close').d('Close')}
        destroyOnClose
      >
        <p>
          {intl
            .get('hostConnect.integration.csvModal.description')
            .d(`Enter your email address and we will send the ${typeLabel} CSV when it is ready.`)}
        </p>
        {activeFilters.length > 0 && (
          <p>{intl.get('hostConnect.integration.csvModal.filteredExport').d(`Current filters will be applied (${activeFilters.join(', ')}).`)}</p>
        )}
        <Form layout="vertical">
          <Form.Item label={intl.get('hostConnect.integration.csvModal.email').d('Email')}>
            {getFieldDecorator('email', {
              initialValue: this.props.defaultEmail || '',
              normalize: normalizeEmail,
              rules: [
                {
                  required: true,
                  message: intl.get('hostConnect.integration.csvModal.emailRequired').d('Email is required')
                },
                {
                  pattern: new RegExp(EMAIL_REGEX, 'i'),
                  message: intl.get('hostConnect.integration.csvModal.emailInvalid').d('Please enter a valid email')
                }
              ]
            })(<Input placeholder={intl.get('hostConnect.integration.csvModal.emailPlaceholder').d('Enter your email')} />)}
          </Form.Item>
        </Form>
      </Modal>
    );
  }
}

export default Form.create()(IntegrationCsvModal);
